import { Component } from 'react';
import styled from 'styled-components';
import { theme } from '../theme';
import { Icon } from '../Icon/Icon';
import { Label } from '../Label/Label';
import { Tooltip } from '../Tooltip/Tooltip';

const ToggleContainerElement = styled.div<Partial<IProps>>`
  display: flex;
  margin: ${props => (props.margin ? props.theme.vars.newForm.item.margin : 0)};
  label {
    margin-left: 15px !important;
    margin-right: ${props => (props.tooltip ? '15px' : 0)} !important;
    margin-bottom: 0 !important;
  }
`;

const ToggleElement = styled.div`
  position: relative;
  width: 35px;
  height: 20px;
  :hover {
    .slider {
      background-color: ${props => props.theme.vars.newColor.darkGS2};
      .toggle-thumb svg {
        color: ${props => props.theme.vars.newColor.darkGS2};
      }
    }
    input:checked + .slider {
      background-color: ${props => props.theme.vars.newColor.blue2};
      .toggle-thumb svg {
        color: ${props => props.theme.vars.newColor.darkGS2};
      }
    }
  }

  /* Hide default HTML checkbox */
  input {
    opacity: 0;
    position: absolute;
    width: 100%;
    height: 100%;
    margin: 0;
    cursor: pointer;
    z-index: 1;
  }

  .slider {
    transition: 0.2s;
    background-color: ${props => props.theme.vars.newColor.darkGS3};
    width: 100%;
    height: 100%;
    border-radius: 30px;
    cursor: pointer;
    .toggle-thumb {
      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
      content: '';
      height: 20px;
      width: 20px;
      transform: scale(1.2);
      left: 0;
      bottom: 0;
      background-color: white;
      transition: left 0.2s;
      border-radius: 50%;
      border: 1px solid ${props => props.theme.vars.newColor.darkGS3};
      svg {
        color: ${props => props.theme.vars.newColor.darkGS2};
      }
    }
  }

  input:checked + .slider {
    background-color: ${props => props.theme.vars.newColor.blue1};
  }

  input:checked:hover + .slider .toggle-thumb {
    border: 1px solid ${props => props.theme.vars.newColor.darkGS3};
  }

  input:focus + .slider .toggle-thumb {
    box-sizing: border-box;
    border: 1px solid ${props => props.theme.vars.newColor.darkGS3};
  }

  input:hover + .slider .toggle-thumb {
    box-sizing: border-box;
    border: none;
    border: 1px solid ${props => props.theme.vars.newColor.darkGS2};
  }

  input:checked + .slider .toggle-thumb {
    left: 15px;
  }

  input:disabled + .slider {
    background-color: ${props => props.theme.vars.newColor.lightGS1};
    cursor: default;
    .toggle-thumb {
      background-color: ${props => props.theme.vars.newColor.lightGS2};
    }
  }

  input:disabled:hover + .slider .toggle-thumb {
    border: 1px solid ${props => props.theme.vars.newColor.darkGS3};
  }

  input:disabled {
    cursor: default;
  }
`;

export interface IProps {
  /** Input ARIA label tag */
  ariaLabel: string;
  /** The label name for the checkbox */
  label?: string;
  /** Will be passed through to the labels for attribute */
  inputId?: string;
  /** onChange is invoked whenever the toggle is changed */
  onChange?: (checked: boolean) => void;
  /** Is the checkbox checked */
  checked?: boolean;
  /** Disable the toggle */
  disabled?: boolean;
  /** Adds the default margin styles for form items (default: true)  */
  margin?: boolean;
  /** The tabIndex of the checkbox */
  tabIndex?: number;
  /** Changes the color of the label */
  color?: string;
  /** Optional tooltip to display */
  tooltip?: string;
  /** Optional testId for testing component*/
  testId?: string;
}

export class Toggle extends Component<IProps> {
  public static defaultProps: Pick<IProps, 'margin'> = {
    margin: true,
  };
  public render() {
    return (
      <ToggleContainerElement margin={this.props.margin} tooltip={this.props.tooltip}>
        <ToggleElement>
          <input
            type="checkbox"
            checked={this.props.checked}
            tabIndex={this.props.tabIndex}
            disabled={this.props.disabled}
            onMouseUp={this.handleMouseUp}
            onChange={this.handleChange}
            data-testid={this.props.testId || 'CheckboxElement'}
            id={this.props.inputId}
            aria-label={this.props.ariaLabel}
          />
          <div className="slider">
            <span className={`toggle-thumb ${this.props.disabled ? 'disabled' : null}`}>
              {!this.props.disabled ? (
                this.props.checked ? (
                  <Icon icon="check" size="small" />
                ) : (
                  <Icon icon="times" size="small" />
                )
              ) : null}
            </span>
          </div>
        </ToggleElement>
        {this.props.label ? (
          <Label
            name={this.props.label}
            disabled={this.props.disabled}
            variation="form"
            color={this.props.color}
            for={this.props.inputId}
          />
        ) : null}
        {this.props.tooltip && (
          <Tooltip html={this.props.tooltip}>
            <Icon icon="question-circle" size="small" color={theme.vars.newColor.darkGS2} />
          </Tooltip>
        )}
      </ToggleContainerElement>
    );
  }

  private handleChange = (e: any) => {
    if (this.props.onChange) {
      this.props.onChange(e.target.checked);
    }
  };

  private handleMouseUp = (e: any) => {
    e.target.blur();
  };
}
